'use client'

import { formatImageURL } from "@/utils/util"
import { getInitials } from "@/utils/avatar"
import { PASSWORD_RULES } from "@/utils/auth"
import { useCustomerSettings, useChangePassword } from "@/lib/commerce/hooks"
import { Check, Minus } from "lucide-react"
import { TextField } from "../elements/Field"
import { panelFocusRing } from "../elements/SidePanel"

const solidButton = `h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${panelFocusRing}`
const textAction = `text-[13px] pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed ${panelFocusRing}`

function SettingsSection({ title, description, children }: { title: string, description: string, children: React.ReactNode }) {
  return (
    <section className="py-10 border-b border-border lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-12">
      <div className="mb-6 lg:mb-0">
        <h2 className="text-base text-foreground">{title}</h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  )
}

export default function Settings({ customer }: { customer: any, vendor: any }) {
  const {
    firstName, setFirstName, lastName, setLastName, email, phone, setPhone,
    preview, loading, isImageUploaded, inputFile,
    handleFileChange, selectFile, handleSave,
  } = useCustomerSettings(customer)

  const {
    newPassword, setNewPassword, newPasswordConfirm, setNewPasswordConfirm,
    checks, isPasswordLoading, handlePasswordChange,
  } = useChangePassword()

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || customer?.email || ''
  const avatarSrc = preview || (customer?.avatar ? formatImageURL(customer.avatar) : null)

  return (
    <div>
      <div className="pb-6 border-b border-border">
        <h1 className="text-2xl sm:text-3xl tracking-tight text-foreground">Тохиргоо</h1>
        <p className="mt-2 text-sm text-muted-foreground">Өөрийн хувийн мэдээлэл болон нууц үгээ засах</p>
      </div>

      <SettingsSection title="Хувийн мэдээлэл" description="Таны нэр, зураг болон холбоо барих мэдээлэл.">
        <div className="flex items-center gap-5">
          <button type="button" onClick={selectFile} disabled={isImageUploaded} aria-label="Зураг солих" className={`group relative size-16 shrink-0 bg-muted overflow-hidden grid place-items-center text-sm tracking-wide text-muted-foreground ${panelFocusRing}`}>
            {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : getInitials(fullName)}
            <span aria-hidden className="absolute inset-0 grid place-items-center bg-foreground/40 text-[11px] text-background opacity-0 transition-opacity duration-150 group-hover:opacity-100">Солих</span>
          </button>
          <div className="min-w-0">
            <button type="button" onClick={selectFile} disabled={isImageUploaded} className={textAction}>
              {isImageUploaded ? 'Зураг оруулж байна…' : 'Зураг солих'}
            </button>
          </div>
          <input type="file" accept="image/*" ref={inputFile} className="hidden" onChange={handleFileChange} />
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField label="Нэр" placeholder="Таны нэр" value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" />
          <TextField label="Овог" placeholder="Таны овог" value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" />
          <TextField label="И-мэйл" placeholder="example@shop.mn" value={email} disabled description="И-мэйл хаягийг өөрчлөх боломжгүй" />
          <TextField label="Утасны дугаар" placeholder="00000000" value={phone} onChange={e => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" />
        </div>

        <div className="mt-8 flex justify-end">
          <button type="button" onClick={handleSave} disabled={loading} className={solidButton}>{loading ? 'Хадгалж байна…' : 'Хадгалах'}</button>
        </div>
      </SettingsSection>

      <SettingsSection title="Нууц үг солих" description="Нэвтрэхэд ашиглах шинэ нууц үгээ оруулна уу.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField label="Шинэ нууц үг" type="password" placeholder="Шинэ нууц үг" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" />
          <TextField label="Шинэ нууц үг давтах" type="password" placeholder="Шинэ нууц үг давтах" value={newPasswordConfirm} onChange={e => setNewPasswordConfirm(e.target.value)} autoComplete="new-password" />
        </div>

        <ul className="mt-5 space-y-2" aria-label="Нууц үгийн шаардлага">
          {PASSWORD_RULES.map(rule => (
            <li key={rule.key} className={`flex items-center gap-2 text-[13px] transition-colors duration-150 ${checks[rule.key] ? 'text-foreground' : 'text-muted-foreground'}`}>
              {checks[rule.key] ? <Check aria-hidden className="size-3.5" strokeWidth={1.5} /> : <Minus aria-hidden className="size-3.5" strokeWidth={1.5} />}
              <span>{rule.label}</span>
              <span className="sr-only">{checks[rule.key] ? '(хангасан)' : '(хангаагүй)'}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex justify-end">
          <button type="button" onClick={handlePasswordChange} disabled={isPasswordLoading} className={solidButton}>{isPasswordLoading ? 'Хадгалж байна…' : 'Нууц үг солих'}</button>
        </div>
      </SettingsSection>
    </div>
  )
}
